// types/aiEvents.types.ts

// ============================================
// ENUMS
// ============================================

export enum AIGenerationType {
  DESCRIPTION = 'description',
  TITLE = 'title',
  TAGS = 'tags',
  IMAGE = 'image',
  EMAIL = 'email',
  SOCIAL_POST = 'social_post',
  AGENDA = 'agenda',
}

export enum AIGenerationStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum EventTemplateType {
  EVENT = 'event',
  EMAIL = 'email',
}

export enum EventIndustry {
  TECH = 'tech',
  MUSIC = 'music',
  ART = 'art',
  BUSINESS = 'business',
  SPORTS = 'sports',
  HEALTHCARE = 'healthcare',
  EDUCATION = 'education',
  FOOD = 'food',
  FASHION = 'fashion',
  GAMING = 'gaming',
  NETWORKING = 'networking',
  CHARITY = 'charity',
  FILM = 'film',
  REAL_ESTATE = 'real-estate',
  AUTOMOTIVE = 'automotive',
  WEDDING = 'wedding',
  WELLNESS = 'wellness',
  COMEDY = 'comedy',
  PHOTOGRAPHY = 'photography',
  STARTUP = 'startup',
}

export enum EventType {
  CONFERENCE = 'conference',
  WORKSHOP = 'workshop',
  SEMINAR = 'seminar',
  WEBINAR = 'webinar',
  NETWORKING = 'networking',
  CONCERT = 'concert',
  FESTIVAL = 'festival',
  SPORTS = 'sports',
  EXHIBITION = 'exhibition',
  TRAINING = 'training',
  HACKATHON = 'hackathon',
  MASTERCLASS = 'masterclass',
}

export enum EventFormat {
  IN_PERSON = 'in_person',
  VIRTUAL = 'virtual',
  HYBRID = 'hybrid',
}

export enum EventStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  LIVE = 'live',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  POSTPONED = 'postponed',
}

export enum EventVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  UNLISTED = 'unlisted',
  RESTRICTED = 'restricted',
}

// ============================================
// AI GENERATION TYPES
// ============================================

export interface AIGeneration {
  id: string;
  documentId: string;
  organization?: {
    id: string;
    name: string;
  };
  event?: {
    id: string;
    title: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  type: AIGenerationType;
  model: string;
  prompt: string;
  parameters?: Record<string, any>;
  record_status: AIGenerationStatus;
  result?: string;
  image_url?: string;
  variations?: any[];
  credits_used: number;
  processing_time_ms?: number;
  quality_score?: number;
  user_rating?: number;
  feedback?: string;
  approved: boolean;
  approved_by?: {
    id: string;
    name: string;
  };
  approved_at?: string;
  error_message?: string;
  retry_count: number;
  createdAt: string;
  updatedAt: string;
}

export interface AITemplate {
  id: string;
  documentId: string;
  name: string;
  template_type: EventTemplateType;
  industry: EventIndustry;
  structure: EventStructure;
  preview_image?: {
    id: string;
    url: string;
  };
  is_active: boolean;
  usage_count: number;
  description?: string;
  events?: Array<{ id: string; title: string }>;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

// ============================================
// EVENT STRUCTURE FOR AI GENERATION
// ============================================
export interface PageSection {
  id: string;
  component: string;
  visible: boolean;
  order: number;
  props?: Record<string, any>;
  content?: any;
}

export interface EventLocation {
  name: string;
  address: string;
  city: string;
  state?: string;
  country: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  virtual_link?: string;
}

export interface EventStructure {
  // Basic Info
  title: string;
  subtitle?: string;
  description: string;
  short_description?: string;
  event_type: EventType;
  format: EventFormat;

  // Categorization
  industry: EventIndustry;
  tags?: string[];
  topics?: string[];

  // Scheduling
  start_date: string; // ISO datetime
  end_date: string;
  timezone: string;
  doors_open_at?: string;
  registration_starts_at?: string;
  registration_ends_at?: string;
  early_bird_ends_at?: string;

  // Location
  location: EventLocation;
  platform?: string;
  meeting_url?: string;

  // Ticketing
  ticketing_enabled: boolean;
  free_event: boolean;
  base_price?: number;
  currency: string;
  capacity?: number;

  // Organizer
  organizer_name: string;
  organizer_email: string;
  organizer_phone?: string;
  organizer_avatar?: string;
  organizer_website?: string;

  // Content
  images?: Array<{
    url: string;
    alt: string;
  }>;
  featured: boolean;

  // Settings
  visibility: EventVisibility;
  send_confirmations: boolean;

  // AI Metadata
  ai_generated?: boolean;
  ai_model?: string;
  ai_prompt?: string;

  page_sections?: DynamicZoneComponent[];
  sectionOrder?: string[];
}

export interface DynamicZoneComponent {
  __component: string; // e.g., "sections.hero"
  id?: number;
  visible?: boolean; // Frontend-only field
  order?: number; // Frontend-only (array index is order)
  
  // Common fields across all sections
  className?: string;
  customStyles?: Record<string, any>;
  theme?: "light" | "dark" | "gradient" | "custom";
  variant?: "default" | "outlined" | "filled" | "ghost";
  [key: string]: any; // Component-specific fields
}
// ============================================
// REQUEST TYPES
// ============================================

export interface GenerateEventRequest {
  prompt: string;
  industry: EventIndustry;
  event_type?: EventType;
  format?: EventFormat;
  user_preferences?: {
    capacity?: number;
    price_range?: {
      min: number;
      max: number;
    };
    location?: string;
    date_range?: {
      start: string;
      end: string;
    };
  };


}

export interface GenerateTemplateRequest {
  event_id?: string;
  industry: EventIndustry;
  template_type: EventTemplateType;
  custom_sections?: string[];
}

export interface EnhanceDescriptionRequest {
  description: string;
  industry: string;
  event_id: string;
  eventType: string;
  model: string;
  current_description: string;
  tone?: 'professional' | 'casual' | 'exciting' | 'formal';
  length?: 'short' | 'medium' | 'long';
}

export interface AnalyzeEventImageRequest {
  image_url: string;
  event_id?: string;
}

export interface CreateAISessionRequest {
  title?: string;
  context?: string;
  metadata?: Record<string, any>;
}

export interface SendAIMessageRequest {
  session_id: string;
  message: string;
  context?: Record<string, any>;
  model?: string;  // ADD THIS if not present
  includeHistory?: boolean;  // ADD THIS if not present
}

// ============================================
// RESPONSE TYPES
// ============================================

export interface GenerateEventResponse {
  success: boolean;
  event: EventStructure;
  ai_generation: AIGeneration;
  ai_template?: AITemplate;
  saved_event?: {
    id: string;
    documentId: string;
    title: string;
  };
}

export interface GenerateTemplateResponse {
  success: boolean;
  template: AITemplate;
  structure: EventStructure;
}

export interface AISession {
  data: any;
  id: string;
  documentId: string;
  title: string;
  user_id: string;
  context?: string;
  metadata?: Record<string, any>;
  is_active: boolean;
  message_count: number;
  createdAt: string;
  updatedAt: string;
}

export interface AIMessage {
  data: any;
  id: string;
  messages: any;
  documentId: string;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  model?: string;
  tokens?: number;
  metadata?: Record<string, any>;
  createdAt: string;
}

export interface AIModelsResponse {
  data: any;
  models: Array<{
    id: string;
    name: string;
    description: string;
    context_length: number;
    capabilities: string[];
  }>;
}

// ============================================
// STATE TYPES
// ============================================

export interface AIEventsState {
  navigationData: any | null;
  initialPrompt: string | null;
  // Generation state
  currentGeneration: AIGeneration | null;
  isGenerating: boolean;
  generationError: string | null;

  // Event structure
  generatedEvent: EventStructure | null;
  savedEventId: string | null;

  // Template state
  selectedTemplate: AITemplate | null;
  availableTemplates: AITemplate[];
  isLoadingTemplates: boolean;

  // Session state
  currentSession: AISession | null;
  sessions: AISession[];
  messages: AIMessage[];
  isLoadingMessages: boolean;

  // UI state
  showGenerationDialog: boolean;
  showTemplateSelector: boolean;
  generationProgress: number;

  generationId: string | null;
}
